import { Divider, EDividerVariant, GeneralSearchIcon, Text } from '@outdoorsyco/bonfire';
import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import useQueryParams from '@/hooks/useQueryParams';
import { useRouter } from '@/hooks/useRouter';
import {
  getHeaderSearchAddressFilter,
  getHeaderSearchDatesFilter,
  getHeaderSearchGuestsFilter,
} from '@/redux/selectors/globalHeader';
import { trackSearchFilterToggled } from '@/services/analytics/search';
import { ESearchWidgetFilter } from '@/services/analytics/search/types';
import { useExperimentIsEnabled } from '@/services/experiments';
import { OptimizelyFlags } from '@/services/experiments/flags';
import { formatDateFullRange } from '@/utility/format-date';
import { isDeliveryMode } from '@/utility/queryParams';
import { yieldToMain } from '@/utility/yieldToMain';

import { useApplySearch } from './applySearch';
import { DateFilterButton } from './DateFilterButton';
import { DateFilterContent } from './DateFilterContent';
import { GuestsFilterButton } from './GuestsFilterButton';
import { GuestsFilterContent } from './GuestsFilterContent';
import { LocationAutocompleteProvider } from './LocationAutocompleteContext';
import { LocationAutocompleteMenu } from './LocationAutocompleteMenu';
import { LocationFilterContent } from './LocationFilterContent';
import { LocationFilterProvider } from './LocationFilterContext';
import { LocationFilterInput } from './LocationFilterInput';

export enum EHomeSearchWidgetVariant {
  RvRentals,
  StationaryRv,
  AutoRentals,
  Escapes,
}

type THomeSearchWidget = {
  variant: EHomeSearchWidgetVariant;
  desktopClassName?: string;
  mobileClassName?: string;
  className?: string;
};

export const HomeSearchWidget = ({
  variant = EHomeSearchWidgetVariant.RvRentals,
  desktopClassName = '',
  mobileClassName = '',
  className = '',
}: THomeSearchWidget) => {
  const intl = useIntl();
  const router = useRouter();

  const { isAboveDesktop } = useBreakpoint();

  const addressSelection = useAddressSelection();
  const datesSelection = useDatesSelection();
  const totalGuests = useTotalGuestsFromSelection();

  const containerRef = useRef<HTMLDivElement>(null);
  const locationFilterButtonRef = useRef<HTMLDivElement>(null);
  const dateFilterButtonRef = useRef<HTMLDivElement>(null);
  const guestsFilterButtonRef = useRef<HTMLDivElement>(null);

  const [openedFilter, setOpenedFilter] = useState<ESearchWidgetFilter>();

  const userInteractedWithLocationRef = useRef(false);

  const queryParams = useQueryParams();
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );
  const isCurrentlyDeliveryMode = isDeliveryMode(queryParams);

  const setOpenedFilterYTM = (filter?: ESearchWidgetFilter) => {
    yieldToMain(() => setOpenedFilter(filter));

    if (!userInteractedWithLocationRef.current && filter === ESearchWidgetFilter.Location) {
      userInteractedWithLocationRef.current = true;
    }
  };

  const toggleTargetFilter = (targetFilter: ESearchWidgetFilter) => {
    return () => setOpenedFilterYTM(openedFilter === targetFilter ? undefined : targetFilter);
  };

  const [, { update: updatePrevBaseUrl }] = useLocalStorage('prevBaseUrl');
  const applySearch = useApplySearch(variant);

  const handleSearch = async () => {
    setOpenedFilter(undefined);
    updatePrevBaseUrl(router.route);
    await applySearch();
  };

  // Open the next filter in the sequence that is not filled
  const openNextFilter = () => {
    // Because we auto complete the location, it might get confusing for the
    // users who would like to update it (back button might not be that obvious).
    // Keep track of user interaction with the location, and always open the
    // location filter first if the user hasn't interacted with it yet.
    if (!addressSelection || !userInteractedWithLocationRef.current) {
      return setOpenedFilterYTM(ESearchWidgetFilter.Location);
    }

    if (!datesSelection || variant === EHomeSearchWidgetVariant.AutoRentals)
      return setOpenedFilterYTM(ESearchWidgetFilter.Date);
    return setOpenedFilterYTM(ESearchWidgetFilter.Guests);
  };

  const getSearchBarLabel = () => {
    if (variant === EHomeSearchWidgetVariant.StationaryRv) {
      return intl.formatMessage({
        defaultMessage: 'Delivery location',
        id: 'aWbBrJ',
        description: 'Home Search Widget > Delivery Location Filter Placeholder',
      });
    }
    if (variant === EHomeSearchWidgetVariant.Escapes) {
      return intl.formatMessage({
        defaultMessage: 'Location',
        id: '7TGFRI',
        description: 'Home Search Widget > Escapes Location Filter Placeholder',
      });
    }

    // When the flag is enabled, check delivery mode from query params
    if (isRentalsVDeliverySearchModeEnabled && isCurrentlyDeliveryMode) {
      return intl.formatMessage({
        defaultMessage: 'Search a location',
        id: 'ZTfNjq',
        description: 'Home Search Widget > Search Location Filter Placeholder for Delivery',
      });
    }

    return intl.formatMessage({
      defaultMessage: 'Pickup location',
      id: 'Yxgq0K',
      description: 'Home Search Widget > Location Filter Placeholder',
    });
  };

  const getAddressButtonLabel = () => {
    if (addressSelection) return addressSelection;

    if (isRentalsVDeliverySearchModeEnabled) {
      if (variant === EHomeSearchWidgetVariant.StationaryRv) {
        return intl.formatMessage({
          defaultMessage: 'Where do you want the RV delivered?',
          id: 'tfpgUo',
          description: 'Home Search Widget > Mobile StationaryRv Location Filter Label',
        });
      }
      return intl.formatMessage({
        defaultMessage: 'Where do you want to go?',
        id: 'Q2eV23',
        description: 'Home Search Widget > Mobile Location Filter Label',
      });
    }

    return intl.formatMessage({
      defaultMessage: 'Where are you heading?',
      id: 'C9pliC',
      description: 'Global Header > Placeholder text for location button on mobile bar',
    });
  };

  const datesButtonLabel = datesSelection
    ? datesSelection
    : intl.formatMessage({
        defaultMessage: 'Add dates',
        id: 'zP42BU',
        description: 'Global Header > Placeholder text for dates button on mobile bar',
      });

  const guestsButtonLabel = totalGuests
    ? intl.formatMessage(
        {
          defaultMessage: '{totalGuests, plural, one {# guest} other {# guests}}',
          id: 'ErPlEg',
          description: 'Global Header > label for guests button on mobile bar',
        },
        {
          totalGuests: totalGuests,
        },
      )
    : intl.formatMessage({
        defaultMessage: 'Add guests',
        id: 'k/FNcF',
        description: 'Global Header > Placeholder text for guests button on mobile bar',
      });

  const trackFilterToggled = (
    oldSelection: Record<string, unknown>,
    selection: Record<string, unknown>,
    modifiedWidgetFilter: ESearchWidgetFilter,
  ) => {
    trackSearchFilterToggled({
      newSelectedFilters: selection,
      previousSelectedFilters: oldSelection,
      modifiedWidgetFilter,
      source: 'home_search',
    });
  };

  useEffect(() => {
    // We are using dialogs for mobile, so we don't need to handle clicks outside the container
    if (!isAboveDesktop) return;

    const handlePressEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Prevent closing the entire menu, only close the dropdown if it is open
        const overlayExists =
          document.querySelector('.home-search-widget-dropdown-overlay') !== null;

        if (!overlayExists) setOpenedFilterYTM(undefined);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      // The dropdown element is outside the `#app` container.
      // To ensure we don't mistakenly close the filters modal, we check if the click event happens inside `#app`.
      // This prevents unintended closures when interacting with elements outside `#app`.
      const appContainer = document.querySelector('#app');

      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        // To avoid close search when click on search input that is outside the filter container
        appContainer?.contains(event.target as Node)
      ) {
        setOpenedFilterYTM(undefined);
      }
    };

    document.addEventListener('keydown', handlePressEscape, true);
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('keydown', handlePressEscape, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isAboveDesktop]);

  const searchButton = (
    <button
      className="flex items-center justify-center h-full bg-gray-800 rounded-full p-7 shrink-0"
      onClick={handleSearch}>
      <GeneralSearchIcon width={32} height={32} color="white" />
    </button>
  );

  return (
    <LocationFilterProvider onNavigateToRecentSearch={() => setOpenedFilter(undefined)}>
      <div ref={containerRef} className={cn('relative w-full h-[52px] lg:h-[100px]', className)}>
        <div
          className={cn(
            'flex w-full h-full overflow-hidden bg-white rounded-full lg:hidden gap-2',
            mobileClassName,
          )}>
          <button onClick={openNextFilter} className="w-full px-6 py-2 overflow-hidden text-left">
            <div className="flex flex-col items-start">
              <Text className="text-sm font-medium text-left truncate w-[240px]">
                {addressSelection || getAddressButtonLabel()}
              </Text>
              <Text className="flex items-center text-xs text-gray-50 gap-2">
                {datesButtonLabel}
                <span className="rounded-full w-0.5 h-0.5 bg-[#4F5B69]" />
                {guestsButtonLabel}
              </Text>
            </div>
          </button>

          <button
            className="flex items-center justify-center w-10 h-10 bg-gray-800 rounded-full m-1.5 shrink-0"
            onClick={handleSearch}>
            <GeneralSearchIcon width={21} height={21} color="white" />
          </button>
        </div>

        <form
          onSubmit={async e => {
            e.preventDefault();
            await handleSearch();
          }}
          className={cn('hidden lg:flex items-center bg-white rounded-full', desktopClassName)}>
          <div
            className={cn('flex-[1] pr-2 pl-8 rounded-l-full', {
              'z-60': openedFilter === ESearchWidgetFilter.Location,
            })}
            ref={locationFilterButtonRef}>
            <LocationAutocompleteProvider
              variant={variant}
              isOpened={isAboveDesktop && openedFilter === ESearchWidgetFilter.Location}
              onOpen={() => setOpenedFilterYTM(ESearchWidgetFilter.Location)}
              onDataSelection={() => setOpenedFilterYTM(ESearchWidgetFilter.Date)}
              onTrackFilterToggled={trackFilterToggled}>
              <LocationFilterInput
                label={
                  variant === EHomeSearchWidgetVariant.StationaryRv
                    ? intl.formatMessage({
                        defaultMessage: 'Delivery location',
                        id: '0E8byJ',
                        description: 'Home Search Widget > StationaryRv Location Filter Label',
                      })
                    : intl.formatMessage({
                        defaultMessage: 'Location',
                        id: 'HJy2Bx',
                        description: 'Home Search Widget > Location Filter Label',
                      })
                }
                searchBarLabel={getSearchBarLabel()}
              />
              <LocationAutocompleteMenu />
            </LocationAutocompleteProvider>
          </div>

          <Divider height="100px" variant={EDividerVariant.Vertical} />

          <div
            className={cn('flex-[1]', {
              'z-60': openedFilter === ESearchWidgetFilter.Date,
              'px-2': variant !== EHomeSearchWidgetVariant.AutoRentals,
            })}
            ref={dateFilterButtonRef}>
            <div
              className={cn('flex items-center', {
                'pl-2 pr-1.5 gap-4': variant === EHomeSearchWidgetVariant.AutoRentals,
              })}>
              <DateFilterButton toggleFilter={toggleTargetFilter(ESearchWidgetFilter.Date)} />
              {variant === EHomeSearchWidgetVariant.AutoRentals && searchButton}
            </div>
          </div>

          {variant !== EHomeSearchWidgetVariant.AutoRentals && (
            <>
              <Divider height="100px" variant={EDividerVariant.Vertical} />

              <div
                className={cn('flex-[1]', {
                  'z-60': openedFilter === ESearchWidgetFilter.Guests,
                })}
                ref={guestsFilterButtonRef}>
                <div className="flex items-center pl-2 pr-1.5 gap-4 rounded-r-[inherit]">
                  <GuestsFilterButton
                    toggleFilter={toggleTargetFilter(ESearchWidgetFilter.Guests)}
                  />
                  {searchButton}
                </div>
              </div>
            </>
          )}
        </form>

        <LocationFilterContent
          variant={variant}
          onNext={() => setOpenedFilterYTM(ESearchWidgetFilter.Date)}
          nextButtonLabel={intl.formatMessage({
            defaultMessage: 'Next: enter dates',
            id: 'fUbsaE',
            description: 'Home Search Widget > Next to dates',
          })}
          isOpened={!isAboveDesktop && openedFilter === ESearchWidgetFilter.Location}
          onClose={() => setOpenedFilterYTM(undefined)}
          fullHeight={variant === EHomeSearchWidgetVariant.AutoRentals}
          onDataSelection={() => setOpenedFilterYTM(ESearchWidgetFilter.Date)}
          buttonRef={locationFilterButtonRef}
          searchBarRef={containerRef}
          align="left"
        />
        <DateFilterContent
          onDataSelection={() => {
            // Move automatically only for desktop view, mobile has its own controls.
            if (isAboveDesktop && variant !== EHomeSearchWidgetVariant.AutoRentals)
              setOpenedFilterYTM(ESearchWidgetFilter.Guests);
          }}
          onBack={() => setOpenedFilterYTM(ESearchWidgetFilter.Location)}
          {...(variant === EHomeSearchWidgetVariant.AutoRentals
            ? {
                onSearch: handleSearch,
              }
            : {
                onNext: () => setOpenedFilterYTM(ESearchWidgetFilter.Guests),
                nextButtonLabel: intl.formatMessage({
                  defaultMessage: 'Next: guest details',
                  id: 'bOq8a+',
                  description: 'Home Search Widget > Next to guests',
                }),
              })}
          isOpened={openedFilter === ESearchWidgetFilter.Date}
          onClose={() => setOpenedFilterYTM(undefined)}
          fullHeight={variant === EHomeSearchWidgetVariant.AutoRentals}
          onTrackFilterToggled={trackFilterToggled}
          allowSameDay={variant === EHomeSearchWidgetVariant.AutoRentals}
          buttonRef={dateFilterButtonRef}
          searchBarRef={containerRef}
          align="center"
        />

        <GuestsFilterContent
          onBack={() => setOpenedFilterYTM(ESearchWidgetFilter.Date)}
          onSearch={handleSearch}
          isOpened={openedFilter === ESearchWidgetFilter.Guests}
          onClose={() => setOpenedFilterYTM(undefined)}
          onTrackFilterToggled={trackFilterToggled}
          buttonRef={guestsFilterButtonRef}
          searchBarRef={containerRef}
          align="left"
        />
      </div>
    </LocationFilterProvider>
  );
};

const useAddressSelection = () => {
  return useSelector(getHeaderSearchAddressFilter);
};

const useDatesSelection = () => {
  const dates = useSelector(getHeaderSearchDatesFilter);

  if (dates?.from && dates?.to) {
    return `${formatDateFullRange(dates.from.toString(), dates.to.toString())} ${
      dates?.flexible_days ? ` (±${dates?.flexible_days})` : ''
    }`;
  }
};

const useTotalGuestsFromSelection = () => {
  const guests = useSelector(getHeaderSearchGuestsFilter);

  return (guests?.adults || 0) + (guests?.children || 0) + (guests?.infants || 0) || 0;
};
